"use client";

import { useEffect, useState } from "react";

interface ClientOnlyProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * ClientOnly component ensures that children are only rendered on the client side
 * after the component has mounted. This prevents SSR/hydration mismatches.
 * 
 * Triple safety check:
 * 1. Check if window is defined (client-side)
 * 2. Use useState to track mount status
 * 3. Use useEffect to set mounted flag
 */
export function ClientOnly({ children, fallback = null }: ClientOnlyProps) {
  // First check: Are we in the browser?
  const isClient = typeof window !== "undefined";
  
  // Second check: Has the component mounted?
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    // Third check: Set mounted flag after first render
    setHasMounted(true);
  }, []);

  // Don't render children unless ALL checks pass
  if (!isClient || !hasMounted) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}
